import { useRef, useState } from "react";
import { ImagePlus, Loader2, X, Link2 } from "lucide-react";
import api from "@/lib/api";
import { toast } from "sonner";

const BACKEND = process.env.REACT_APP_BACKEND_URL;

export async function uploadImage(file) {
  const fd = new FormData();
  fd.append("file", file);
  const res = await api.post("/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
  return `${BACKEND}${res.data.url}`;
}

export function ImageUpload({ value, onChange, aspect = "aspect-[16/9]", label = "Upload image", testid }) {
  const ref = useRef(null);
  const [busy, setBusy] = useState(false);
  const [urlMode, setUrlMode] = useState(false);
  const [urlText, setUrlText] = useState("");

  const pick = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setBusy(true);
    try {
      const url = await uploadImage(file);
      onChange(url);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Upload failed");
    } finally {
      setBusy(false);
      if (ref.current) ref.current.value = "";
    }
  };

  const applyUrl = () => {
    const u = urlText.trim();
    if (!u) return;
    onChange(u);
    setUrlText(""); setUrlMode(false);
  };

  return (
    <div>
      <input ref={ref} type="file" accept="image/*" className="hidden" onChange={pick} data-testid={`${testid}-input`} />
      {value ? (
        <div className={`relative ${aspect} border border-border overflow-hidden group`}>
          <img src={value} alt="" className="w-full h-full object-cover" />
          <button type="button" onClick={() => onChange("")} data-testid={`${testid}-remove`}
            className="absolute top-2 right-2 bg-background/90 border border-border p-1.5 hover:text-destructive transition-colors">
            <X size={14} />
          </button>
        </div>
      ) : urlMode ? (
        <div className={`w-full ${aspect} border border-dashed border-border flex flex-col items-center justify-center gap-2 p-4`}>
          <input value={urlText} onChange={(e) => setUrlText(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && applyUrl()}
            placeholder="Paste an image URL…" autoFocus data-testid={`${testid}-url-input`}
            className="w-full bg-transparent border border-border px-3 py-2 text-sm outline-none focus:border-gold transition-colors" />
          <div className="flex items-center gap-3 text-xs">
            <button type="button" onClick={applyUrl} data-testid={`${testid}-url-apply`}
              className="text-gold hover:underline">Use image</button>
            <button type="button" onClick={() => { setUrlMode(false); setUrlText(""); }}
              className="text-muted-foreground hover:text-foreground">Cancel</button>
          </div>
        </div>
      ) : (
        <div className={`w-full ${aspect} border border-dashed border-border flex flex-col items-center justify-center gap-2 text-muted-foreground`}>
          <button type="button" onClick={() => ref.current?.click()} disabled={busy} data-testid={`${testid}-button`}
            className="flex flex-col items-center gap-2 hover:text-foreground transition-colors disabled:opacity-50">
            {busy ? <Loader2 size={20} className="animate-spin" /> : <ImagePlus size={20} />}
            <span className="text-xs">{busy ? "Uploading…" : label}</span>
          </button>
          <button type="button" onClick={() => setUrlMode(true)} data-testid={`${testid}-url-toggle`}
            className="inline-flex items-center gap-1 text-[11px] hover:text-gold transition-colors">
            <Link2 size={11} /> or paste a URL
          </button>
        </div>
      )}
    </div>
  );
}
